'use client';

import { Language } from '@coolblue-development/constants';
import { Flex, freddyTheme, Tooltip, Typography } from '@coolblue-development/freddy';
import selectSwitchLanguageTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentHeader/selectSwitchLanguageTrigger';
import { Checkmark } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { generateWebsiteUrl } from '@utils/url-generators/generateWebsiteUrl';
import Link from 'next/link';

import { PopoverForDisabledLanguage } from '../PopoverForDisabledLanguage';
import { tooltipTextMap } from '../tooltipText';

export function LanguageOption({
  languageOptionText,
  slug,
  language,
  isDisabled,
  isHamburgerVariant,
  isSelected
}: {
  languageOptionText: string;
  slug?: string;
  language: Language;
  isDisabled: boolean;
  isHamburgerVariant: boolean;
  isSelected: boolean;
}) {
  const { trackEvent } = useAnalytics();
  const { subsidiary, language: pageLanguage } = useRequestContext();

  const url = slug ? generateWebsiteUrl(slug, subsidiary, language) : '';

  const languageOptionContent = (
    <Flex
      key={languageOptionText}
      alignItems='center'
      justifyContent='space-between'
      aria-disabled={!!isDisabled}
      sx={{
        padding: `${freddyTheme.spacing[200]} ${freddyTheme.spacing[300]}`,
        backgroundColor: `${
          isSelected ? freddyTheme.color.background.interactiveSelected : 'transparent'
        }`,
        borderRadius: freddyTheme.borderRadius[100],
        '&:hover': {
          backgroundColor: freddyTheme.color.background[isDisabled ? 'base' : 'interactiveHovered']
        }
      }}
    >
      <Typography
        marginBottom={0}
        sx={{
          color: freddyTheme.color.font[isDisabled ? 'interactiveDisabled' : 'base']
        }}
      >
        {languageOptionText}
      </Typography>
      {isSelected && <Checkmark color={freddyTheme.color.icon.base} />}
      {isDisabled && isHamburgerVariant && <PopoverForDisabledLanguage subsidiary={subsidiary} />}
    </Flex>
  );

  if (isDisabled) {
    if (!isHamburgerVariant) {
      return (
        <Tooltip description={tooltipTextMap[subsidiary]} placement='left'>
          {languageOptionContent}
        </Tooltip>
      );
    }
    return languageOptionContent;
  }

  return (
    <Link
      href={url}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={e => {
        if (isSelected || !slug) {
          e.preventDefault();
          return;
        }
        trackEvent(
          selectSwitchLanguageTrigger({
            previous_language: pageLanguage,
            new_language: language
          })
        );
        window.location.href = url;
      }}
    >
      {languageOptionContent}
    </Link>
  );
}
